import { ProgressFeed } from "#src/routes/investigations/[investigationId]/ProgressFeed";
import { Button, Icon } from "@validereinc/common-components";
import { Investigations } from "@validereinc/domain-controllers";
import { type InvestigationProgressEventType } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useState } from "react";
import { formatInvestigationProgressDisplayText } from "#src/routes/investigations/[investigationId]/progressDisplay.helpers";
import styles from "./InvestigationDetailPage.module.scss";

const cx = classNames.bind(styles);

const STARTING_PHASE = "Starting";
const STARTING_DETAIL = "Waiting for the agent to begin...";

export const ProgressTickerVariant = {
  AGENT_TURN: "agent_turn",
  TOOL_IN_PROGRESS: "tool_in_progress",
} as const;

export type ProgressTickerVariantType =
  (typeof ProgressTickerVariant)[keyof typeof ProgressTickerVariant];

export type ProgressTickerProps = {
  /** Required for `agent_turn`; ignored for `tool_in_progress`. */
  progress?: InvestigationProgressEventType[];
  variant?: ProgressTickerVariantType;
  /** Optional override; default from Investigations.view label constant. */
  summaryLabel?: string;
};

const getLatestSummary = (
  progress: InvestigationProgressEventType[]
): { phase: string; detail: string } => {
  const latest = progress[progress.length - 1];
  if (!latest) return { phase: STARTING_PHASE, detail: STARTING_DETAIL };
  const rawPhase =
    typeof latest.phase === "string" && latest.phase.length > 0
      ? latest.phase
      : "Working";
  const rawDetail = typeof latest.detail === "string" ? latest.detail : "";
  return {
    phase: formatInvestigationProgressDisplayText(rawPhase),
    detail: formatInvestigationProgressDisplayText(rawDetail),
  };
};

export const ProgressTicker = ({
  progress = [],
  variant = ProgressTickerVariant.AGENT_TURN,
  summaryLabel,
}: ProgressTickerProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isToolInProgressVariant =
    variant === ProgressTickerVariant.TOOL_IN_PROGRESS;
  const { phase, detail } = isToolInProgressVariant
    ? {
        phase: summaryLabel ?? Investigations.view.ToolInProgressTickerLabel,
        detail: "",
      }
    : getLatestSummary(progress);
  const stepCount = progress.length;
  const hasSteps = stepCount > 0;

  return (
    <div
      className={cx("progress-ticker")}
      data-testid="progress-ticker"
      data-variant={variant}
    >
      <div className={cx("progress-ticker__strip")}>
        <span
          className={cx("progress-feed__dot", "progress-feed__dot--latest")}
          aria-hidden
        />
        <div
          className={cx("progress-ticker__text")}
          data-testid="progress-ticker-summary"
        >
          <span className={cx("progress-ticker__phase")}>{phase}</span>
          {!isToolInProgressVariant && detail ? (
            <>
              <span
                className={cx("progress-ticker__separator")}
                aria-hidden
              >
                ·
              </span>
              <span
                className={cx("progress-ticker__detail")}
                title={detail}
              >
                {detail}
              </span>
            </>
          ) : null}
        </div>
        {!isToolInProgressVariant ? (
          <Button
            variant="text"
            size="small"
            className={cx("progress-ticker__toggle")}
            slotRight={
              <Icon variant={isExpanded ? "caret-up" : "caret-down"} />
            }
            onClick={() => {
              setIsExpanded((open) => !open);
            }}
            aria-expanded={isExpanded}
            aria-controls="progress-ticker-timeline"
          >
            {isExpanded
              ? "Hide steps"
              : hasSteps
                ? `Show steps (${stepCount})`
                : "Show steps"}
          </Button>
        ) : null}
      </div>
      {!isToolInProgressVariant && isExpanded ? (
        <div
          id="progress-ticker-timeline"
          className={cx("progress-ticker__timeline")}
          data-testid="progress-ticker-timeline"
        >
          <ProgressFeed progress={progress} />
        </div>
      ) : null}
    </div>
  );
};
